import StreetViewThumb from '../../../../components/StreetViewThumb/StreetViewThumb';
import { getDisplay, getInstructions, getListingName, getRowRate, getRowSpots } from '../../listingDisplay';
import { getStatusPillMeta, hasGeocodeWarning } from '../../groupings';
import { isBulkDraftListing } from '../drawerEditHelpers';
import { flattenInstructions, MAX_FLATTENED_INSTRUCTIONS_LEN } from '../../../../utils/parkingInstructions';

function GeocodeBadge({ listing }) {
    if (hasGeocodeWarning(listing)) {
        return <span className="badge-geocode warn">⚠ Could not geocode — review</span>;
    }
    if (listing.addressData?.geocode === 'ok') {
        return (
            <span className="badge-geocode ok">
                <svg className="flat-check" width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" style={{ verticalAlign: '-1px' }}>
                    <polyline points="2,7 6,11 12,3" />
                </svg>
                {' '}
                Geocoded
            </span>
        );
    }
    return null;
}

function SummaryCell({ label, value, fullWidth = false, missing = false, badge = null, children = null }) {
    const cellClass = [
        fullWidth ? 'host-summary-cell-full' : '',
        missing ? 'required-missing' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={cellClass || undefined}>
            <div className="host-summary-label">
                {label}
                {badge}
            </div>
            {children ?? (
                <div className="host-summary-value">{value || 'Not set'}</div>
            )}
        </div>
    );
}

export default function SummaryGrid({
    listing,
    editMode = false,
    previewMode = false,
    dirtyFields,
    setDirtyFields,
    fieldErrors,
}) {
    if (!listing) return null;

    const isBulkDraft = isBulkDraftListing(listing);
    const { name, type, address } = getDisplay(listing);
    const spots = getRowSpots(listing);
    const rate = editMode && dirtyFields?.pricing
        ? dirtyFields.pricing.hourlyRate
        : getRowRate(listing);
    const instructions = getInstructions(listing);
    const statusMeta = getStatusPillMeta(listing);

    const hourlyRateValue = dirtyFields?.pricing?.hourlyRate;
    const hourlyRateInputValue = hourlyRateValue != null && hourlyRateValue !== ''
        ? String(hourlyRateValue)
        : '';

    return (
        <div className="host-panel-section">
            <div className="host-panel-section-label">Property summary</div>
            <div className="host-summary">
                {isBulkDraft ? (
                    <div className="host-summary-cell-full">
                        <div className="host-summary-label">
                            Address
                            <GeocodeBadge listing={listing} />
                            <span className="address-readonly-tag">Read-only</span>
                        </div>
                        <div className="host-summary-value">{address || '—'}</div>
                        <p className="address-change-note">
                            Address changes require a new listing — start the wizard again to relist at a different location.
                        </p>
                        <StreetViewThumb
                            lat={listing.addressData?.lat}
                            lng={listing.addressData?.lng}
                        />
                    </div>
                ) : (
                    <SummaryCell label="Address" value={address} fullWidth badge={<GeocodeBadge listing={listing} />} />
                )}
                {!previewMode && (
                    <SummaryCell label="Property name" value={name || getListingName(listing)} fullWidth />
                )}
                {!previewMode && (
                    <SummaryCell label="Type" value={type} missing={!type} />
                )}
                {!previewMode && (
                    <SummaryCell label="Spots" value={spots != null ? String(spots) : null} missing={spots == null} />
                )}
                <SummaryCell
                    label="Hourly rate"
                    missing={rate == null}
                >
                    {editMode ? (
                        <div className={`host-edit-rate-wrap${fieldErrors?.hourlyRate ? ' field-error' : ''}`}>
                            <span className="host-edit-rate-prefix">$</span>
                            <input
                                type="number"
                                min="0"
                                step="0.25"
                                value={hourlyRateInputValue}
                                placeholder="0.00"
                                aria-label="Hourly rate"
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    setDirtyFields((prev) => ({
                                        ...prev,
                                        pricing: {
                                            ...prev.pricing,
                                            hourlyRate: raw === '' ? null : Number(raw),
                                        },
                                    }));
                                }}
                            />
                        </div>
                    ) : (
                        <div className="host-summary-value">
                            {rate != null ? `$${Number(rate).toFixed(2)}` : 'Not set'}
                        </div>
                    )}
                </SummaryCell>
                <SummaryCell label="Status" value={statusMeta.label} />
                {!previewMode && (
                    <SummaryCell label="Parking instructions" fullWidth>
                        {editMode ? (
                            <InstructionsField
                                listing={listing}
                                dirtyFields={dirtyFields}
                                setDirtyFields={setDirtyFields}
                                fieldErrors={fieldErrors}
                            />
                        ) : (
                            <div className="host-summary-value" style={{ whiteSpace: 'pre-wrap' }}>
                                {instructions || '—'}
                            </div>
                        )}
                    </SummaryCell>
                )}
            </div>
        </div>
    );
}

// PP-492: was a flat 1000, but flattenInstructions() can legitimately produce
// up to ~2100 chars for a full 4-approach structured value (500 chars each
// plus a "From <roadLabel>: " prefix) — a smaller cap here silently
// truncated real, previously-confirmed per-road guidance on the next edit.
// Matches the server-side cap in routes/listings.js.
const MAX_INSTRUCTIONS_LEN = MAX_FLATTENED_INSTRUCTIONS_LEN;

function InstructionsField({ listing, dirtyFields, setDirtyFields, fieldErrors }) {
    // Stage edits in dirtyFields.aiFields (and mark the state 'edited') so
    // buildSavePayload picks them up — it iterates aiFields and gates each
    // entry on aiFieldStates[key] ∈ {confirmed, edited, upload}. Writing to
    // dirtyFields.confirmedFields here would render correctly in the textarea
    // but be silently dropped on save (no reader in the payload builder), so
    // the host would see their edit revert after the PUT round-trip.
    // PP-492: normally already a flat string (parkingInstructions syncs from
    // confirmedFields.instructions, which could be the new structured
    // object) — flattenInstructions is a defensive no-op on the common case
    // and prevents a raw object reaching this textarea's `value` (React
    // refuses to render an object as an input value) in any race/edge case.
    const savedValue = flattenInstructions(
        listing?.parkingInstructions ?? listing?.confirmedFields?.instructions ?? ''
    );
    const value = dirtyFields?.aiFields?.instructions ?? savedValue;
    const remaining = MAX_INSTRUCTIONS_LEN - value.length;

    const handleChange = (next) => {
        const truncated = String(next).slice(0, MAX_INSTRUCTIONS_LEN);
        setDirtyFields((prev) => ({
            ...prev,
            aiFields: {
                ...(prev?.aiFields || {}),
                instructions: truncated,
            },
            aiFieldStates: {
                ...(prev?.aiFieldStates || {}),
                instructions: 'edited',
            },
        }));
    };

    return (
        <>
            <textarea
                className="host-edit-textarea"
                rows={4}
                maxLength={MAX_INSTRUCTIONS_LEN}
                value={value}
                onChange={(e) => handleChange(e.target.value)}
                placeholder="How should drivers find and access this spot?"
                aria-label="Parking instructions"
            />
            <div className="host-edit-helper">{remaining} characters left</div>
            {fieldErrors?.instructions && (
                <div className="drawer-field-error">{fieldErrors.instructions}</div>
            )}
        </>
    );
}
